import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { MaxIntensity, MaxTemperature } from '@models';
import { GridContainerComponent } from '../../atoms/containers/grid-container.component';
import { IconComponent } from '../../atoms/icon/icon.component';
import { LoaderWrapperComponent } from '../../atoms/loader-wrapper/loader-wrapper.component';
import { AlarmsPriorityCardComponent } from '../../molecules/alarms-priority-card/alarms-priority-card.component';
import { MaxIntensityCardComponent } from '../../molecules/max-intensity-card/max-intensity-card.component';
import { MaxTemperatureCardComponent } from '../../molecules/max-temperature-card/max-temperature-card.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlarmsPriorityCardComponent,
    MaxIntensityCardComponent,
    MaxTemperatureCardComponent,
    GridContainerComponent,
    IconComponent,
    TranslocoDirective,
    LoaderWrapperComponent,
  ],
  selector: 'ui-home-header',
  template: `
    <ng-container *transloco="let t">
      <div class="relative h-full w-full rounded-lg">
        <div
          class="absolute top-0 left-0 right-0 bg-neutral-light h-[204px]"
        ></div>
        <div class="relative w-full">
          <ui-grid-container>
            <!-- Notifications Section -->
            <div class="col-span-12 sm:col-span-4">
              <div class="card-container bg-[#86a5c2]">
                <div class="text-white py-2 flex flex-row gap-x-2">
                  <ui-icon icon="clock-circle" />
                  <span>{{ t('LATEST_NOTIFICATIONS') }}</span>
                </div>
                <ui-loader
                  class="w-full h-full"
                  [loading]="loadingAlarmsPriority()"
                  loadingType="skeletonCard"
                >
                  <ui-alarms-priority-card
                    [alarmsHighPriorityCount]="alarmsHigh()"
                    [alarmsMediumPriorityCount]="alarmsMedium()"
                    [alarmsLowPriorityCount]="alarmsLow()"
                    (alarmsLinkClick)="handleAlarmsLinkClick()"
                  />
                </ui-loader>
              </div>
            </div>

            <!-- Last Week Section -->
            <div class="col-span-12 sm:col-span-8">
              <div class="relative card-container">
                <div
                  class="rounded-t-lg bg-[#86a5c2] absolute top-0 left-0 w-full h-[188px]"
                ></div>
                <div class="z-10 text-white py-2 flex flex-row gap-x-2">
                  <ui-icon icon="calendar" />
                  <span>{{ t('LAST_WEEK') }}</span>
                </div>
                <div
                  class="z-10 grid grid-cols-1 sm:grid-cols-2 gap-4 h-full w-full"
                >
                  <ui-loader
                    [loading]="loadingMaxIntensity()"
                    loadingType="skeletonCard"
                  >
                    <ui-max-intensity-card
                      [maxIntensityData]="maxIntensityData()"
                      (sendRsabtClick)="handleIntensityRsabtClick($event)"
                      (sendTsabtClick)="handleIntensityTsabtClick($event)"
                    />
                  </ui-loader>
                  <ui-loader
                    [loading]="loadingMaxTemperature()"
                    loadingType="skeletonCard"
                  >
                    <ui-max-temperature-card
                      [maxTemperatureData]="maxTemperatureData()"
                      (sendRsabtClick)="handleTemperatureRsabtClick($event)"
                      (sendTsabtClick)="handleTemperatureTsabtClick($event)"
                    />
                  </ui-loader>
                </div>
              </div>
            </div>
          </ui-grid-container>
        </div>
      </div>
    </ng-container>
  `,
  styles: [
    `
      .card-container {
        @apply flex flex-col justify-center items-center rounded-lg h-full;
      }
    `,
  ],
})
export class HomeHeaderComponent {
  readonly CATEGORY_INTENSITY = 'intensity';
  readonly CATEGORY_TEMPERATURE = 'temperature';

  alarms = input.required<{
    high: number | null;
    medium: number | null;
    low: number | null;
  }>();
  alarmsHigh = computed(() => this.alarms()?.high);
  alarmsMedium = computed(() => this.alarms()?.medium);
  alarmsLow = computed(() => this.alarms()?.low);

  maxIntensityData = input<MaxIntensity | null>(null);
  maxTemperatureData = input<MaxTemperature | null>(null);

  loadingAlarmsPriority = input<boolean>(true);
  loadingMaxIntensity = input<boolean>(true);
  loadingMaxTemperature = input<boolean>(true);

  alarmsLinkClick = output<void>();
  sendIntensityRsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
  }>();
  sendIntensityTsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
  }>();
  sendTemperatureRsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
  }>();
  sendTemperatureTsabtClick = output<{
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
  }>();

  handleAlarmsLinkClick() {
    this.alarmsLinkClick.emit();
  }

  handleTemperatureRsabtClick(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
  }) {
    this.sendTemperatureRsabtClick.emit(event);
  }

  handleTemperatureTsabtClick(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
  }) {
    this.sendTemperatureTsabtClick.emit(event);
  }

  handleIntensityRsabtClick(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
  }) {
    this.sendIntensityRsabtClick.emit(event);
  }

  handleIntensityTsabtClick(event: {
    distributorId: string;
    centerId: string;
    rsabtId: string;
    tsabtId: string;
  }) {
    this.sendIntensityTsabtClick.emit(event);
  }
}
